import { toast } from 'react-toastify'
import { useEffect, useState, useCallback } from 'react'
import { useParams, Link } from 'react-router-dom'

import { PageHeader } from 'components/PageHeader'
import { LoadingCircle } from 'components/Loading'
import { ProtectedSection } from 'components/ProtectedSection'

import { Role } from 'models'
import { show } from '../services/api'
import { DestroyButton } from '../components/Destroy'
import { ToggleActivation } from '../components/ToggleActivation'
import { translations } from '../translations'
import { date } from 'utils'

export function View() {
  const { productId } = useParams() as unknown as { productId: string }

  const [isLoading, setIsLoading] = useState(true)
  const [product, setProduct] = useState<Record<string, any>>()

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        if (!productId) return
        const data = await show(productId)
        setProduct(data)
        setIsLoading(false)
      } catch (err) {
        toast.error('Falha ao carregar produto')
        console.error(err)
      }
    }
    fetchProduct()
  }, [productId])

  const onToggle = useCallback((_id: number, isActive: boolean) => {
    setProduct((prev) => (prev ? { ...prev, active: isActive } : prev))
  }, [])

  const subtypeLabel = () => {
    const subtype = product?.meta?.allowed_subtypes?.[0]
    if (!subtype) return '-'
    return translations.subtype[subtype] ?? subtype
  }

  return (
    <>
      <PageHeader title="Produto" action="Visão geral" />

      {isLoading ? (
        <LoadingCircle />
      ) : (
        <div className="flex flex-row space-x-8 animate-fade-in-down">
          <div className="w-1/2">
            <div>
              <div className="flex justify-between items-center ml-4 mb-4">
                <h3 className="text-xl leading-6 font-medium text-gray-900">
                  {product?.name}
                </h3>
                <div className="flex items-center space-x-2">
                  {product?.active ? (
                    <span className="px-2 inline-flex text-base leading-5 font-semibold rounded-full bg-emerald-100 text-emerald-800">
                      Ativo
                    </span>
                  ) : (
                    <span className="px-2 inline-flex text-base leading-5 font-semibold rounded-full bg-red-100 text-red-800">
                      Não ativo
                    </span>
                  )}
                </div>
              </div>

              <div className="mt-5 border-t border-gray-200">
                <dl className="sm:divide-y sm:divide-gray-200">
                  <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4">
                    <dt className="ml-4 text-sm font-medium text-gray-500">
                      Código
                    </dt>
                    <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                      {product?.code ?? '-'}
                    </dd>
                  </div>
                  <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4">
                    <dt className="ml-4 text-sm font-medium text-gray-500">
                      Tipo
                    </dt>
                    <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                      {translations.type[product?.type as string] ??
                        product?.type ??
                        '-'}
                    </dd>
                  </div>
                  <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4">
                    <dt className="ml-4 text-sm font-medium text-gray-500">
                      Restrição
                    </dt>
                    <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                      {subtypeLabel()}
                    </dd>
                  </div>
                  <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4">
                    <dt className="ml-4 text-sm font-medium text-gray-500">
                      Descrição
                    </dt>
                    <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                      {product?.description ?? '-'}
                    </dd>
                  </div>
                  <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4">
                    <dt className="ml-4 text-sm font-medium text-gray-500">
                      Criado em
                    </dt>
                    <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                      {date(product?.created_at)}
                    </dd>
                  </div>
                  <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4">
                    <dt className="ml-4 text-sm font-medium text-gray-500">
                      Atualizado em
                    </dt>
                    <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                      {date(product?.updated_at)}
                    </dd>
                  </div>
                </dl>
              </div>

              <ProtectedSection roles={[Role.MANAGER]}>
                <div className="mt-6 ml-4 flex space-x-3">
                  <Link
                    to={`/products/${productId}`}
                    className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none"
                  >
                    Editar
                  </Link>
                  <ToggleActivation
                    productId={productId}
                    isActive={product?.active ?? false}
                    onToggle={onToggle}
                  />
                  <DestroyButton id={productId} />
                </div>
              </ProtectedSection>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
